import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlParamReplicaId,
    urlProjectDocTypeIndices,
    urlProjectDocTypeInfo,
    urlProjectDocTypes,
} from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { safeJsonParse } from '../../util/DataUtils';
import { UiElement } from '@eplass/formdesigner/domain';
import { type ActionName } from '../../components/General/DocTable/DocTableActions';
import { IExtendedConfig } from './sliceProjectActions';
import { FileSelectionRule, LoadingState } from '../../types';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocTypeState = {
    data: [],
    loading: false,
    indicesLoading: false,
    error: null,
    docTypeIndicesData: {
        data: null,
        loading: false,
        error: null,
    },
    docTypeInfoState: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available document types for a given project database.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectDocTypes: async (replicaId: string): Promise<IProjectDocTypeResponseItem[]> => {
        try {
            const url = urlProjectDocTypes.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);
            const result = await response.json();
            const mappedResult = result.map(item => {
                return {
                    ...item,
                    detailSchema: safeJsonParse<UiElement>(item?.detailSchema, {} as UiElement),
                };
            });
            return response.ok ? mappedResult : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that fetches the document index convention for the given document type.
     * @param replicaId - ReplicaId for the target project database.
     * @param docType - DocType of the given document.
     * @returns Promise for the HTTP request.
     */
    getProjectDocTypeIndices: async ({ replicaId, docType }): Promise<IDocTypeIndicesData> => {
        try {
            const url = urlProjectDocTypeIndices.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                headers: { docType },
            };
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getProjectDocTypeInfo: async ({ replicaId, docType }): Promise<object> => {
        try {
            const url = urlProjectDocTypeInfo.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                headers: {
                    docTypeKey: docType,
                },
            };
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocTypeState>) => {
        /**
         * Reducers for the 'getProjectDocTypes' thunk.
         */
        builder.addCase(getProjectDocTypes.pending, (state: Draft<IProjectDocTypeState>) => {
            state.data = null;
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectDocTypes.fulfilled,
            (
                state: Draft<IProjectDocTypeState>,
                action: IAction<IProjectDocTypeResponseItem[]>,
            ) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload.map(doctype => {
                    return { ...doctype, extendedConfig: JSON.parse(doctype.extendedConfig) };
                });
            },
        );
        builder.addCase(
            getProjectDocTypes.rejected,
            (state: Draft<IProjectDocTypeState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );

        /**
         * Reducers for the 'getProjectDocTypeIndices' thunk.
         */
        builder.addCase(getProjectDocTypeIndices.pending, (state: Draft<IProjectDocTypeState>) => {
            state.loading = true;
            state.indicesLoading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectDocTypeIndices.fulfilled,
            (state: Draft<IProjectDocTypeState>, action: IAction<IDocTypeIndicesData>) => {
                state.loading = false;
                state.indicesLoading = false;
                state.error = null;
                state.docTypeIndicesData.data = action.payload;
            },
        );
        builder.addCase(
            getProjectDocTypeIndices.rejected,
            (state: Draft<IProjectDocTypeState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.indicesLoading = false;
                state.error = action.error;
                state.docTypeIndicesData.data = null;
            },
        );

        /**
         * Reducers for the 'getProjectDocTypeInfo' thunk.
         */
        builder.addCase(getProjectDocTypeInfo.pending, (state: Draft<IProjectDocTypeState>) => {
            state.docTypeInfoState = 'Pending';
        });
        builder.addCase(
            getProjectDocTypeInfo.fulfilled,
            (state: Draft<IProjectDocTypeState>, action) => {
                state.docTypeInfoState = 'Succeed';

                const i = state.data.findIndex(x => x.name === action.meta.arg.docType);
                state.data[i].additionalInfo = {
                    fileAttachmentsRule: action.payload['fileAttachmentsRule'] as FileSelectionRule,
                    fileDataModelRule: action.payload['fileDataModelRule'] as FileSelectionRule,
                };
            },
        );
        builder.addCase(getProjectDocTypeInfo.rejected, (state: Draft<IProjectDocTypeState>) => {
            state.docTypeInfoState = 'Failed';
        });
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocTypes = createAsyncThunk('project/docTypes', thunks.getProjectDocTypes);
export const getProjectDocTypeIndices = createAsyncThunk(
    'project/docindices',
    thunks.getProjectDocTypeIndices,
);
export const getProjectDocTypeInfo = createAsyncThunk(
    'project/doctypeinfo',
    thunks.getProjectDocTypeInfo,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocTypeState extends SlicePropsWithData<IProjectDocType[]> {
    indicesLoading: boolean;

    /** Index convention list of the given DocType. */
    docTypeIndicesData: SlicePropsWithData<IDocTypeIndicesData>;

    /** State of loading additional info to a given DocType. */
    docTypeInfoState: LoadingState;
}

export interface IDocTypeIndicesData {
    /** List of indices. */
    DocTypeIndices: string[];
}

export interface IDocTypeAdditionalInfo {
    /** Rules for attaching files. */
    fileAttachmentsRule: FileSelectionRule;

    /** Rules for attaching file datamodels. */
    fileDataModelRule: FileSelectionRule;
}

/**
 * Interface that describes a response DocType configuration.
 */
export interface IProjectDocTypeResponseItem<TExtendedConfig = string> {
    /** DocType name. */
    name: string;

    /** DocType SVG Icon. */
    icon: string;

    /** DocType context menu configuration. */
    contextMenu: ActionName[];

    /** DocType hover button configuration. */
    hoverButtons: ActionName[];

    /** DocType SVG Icon color. */
    color?: string;

    /** DocType Display text translations */
    translations: { [id: string]: string };

    /** Schema for the Detail View of the Documenttype */
    detailSchema: string;

    /** Optional config object. */
    extendedConfig?: TExtendedConfig;

    /** Optional additional info. */
    additionalInfo?: IDocTypeAdditionalInfo;
}

/**
 * Interface that describes a DocType configuration.
 */
export type IProjectDocType = IProjectDocTypeResponseItem<IExtendedConfig>;
/**
 * Create the redux slice.
 */
export const projectDocTypeSlice = createSlice({
    name: 'projectDocType',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducer as default.
 */
export default projectDocTypeSlice.reducer;
